import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { API_URL } from '../../../utils/constants';
import './AgentsAddScreen.css';

const AgentsAddScreen = () => {
  const [formData, setFormData] = useState({
    agent_number: '',
    agent_type_id: '',
    agent_commission_id: '',
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    county: '',
    phone1: '',
    phone2: '',
    active: true
  });

  const [agentTypes, setAgentTypes] = useState([]);
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchLookupData();
  }, []);

  const fetchLookupData = async () => {
    try {
      const [typesResponse, commissionsResponse] = await Promise.all([
        fetch(`${API_URL}/agent-types`),
        fetch(`${API_URL}/agent-commissions`)
      ]);

      if (typesResponse.ok) {
        const typesData = await typesResponse.json();
        setAgentTypes(typesData);
      }

      if (commissionsResponse.ok) {
        const commissionsData = await commissionsResponse.json();
        setAgentCommissions(commissionsData);
      }
    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateAgentNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({
      ...prev,
      agent_number: `A${timestamp}${random}`
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agent_number || !formData.name) {
      setError('Agent number and name are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          created_by: 'System'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create agent');
      }

      const result = await response.json();
      setSuccess('Agent created successfully!');
      
      // Reset form
      setFormData({
        agent_number: '',
        agent_type_id: '',
        agent_commission_id: '',
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipcode: '',
        county: '',
        phone1: '',
        phone2: '',
        active: true
      });
    } catch (error) {
      console.error('Error creating agent:', error);
      setError(error.message || 'Error creating agent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      agent_number: '',
      agent_type_id: '',
      agent_commission_id: '',
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
      county: '',
      phone1: '',
      phone2: '',
      active: true
    });
    setError('');
    setSuccess('');
  };

  return (
    <Container fluid className="agents-add-screen">
      <Row className="mb-4">
        <Col>
          <h2>Add New Agent</h2>
          <p className="text-muted">Create a new agent in the system</p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Agent Number *</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          name="agent_number"
                          value={formData.agent_number}
                          onChange={handleInputChange}
                          placeholder="Enter agent number"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={generateAgentNumber}
                          size="sm"
                        >
                          Generate
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Agent Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter agent name"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Agent Type</Form.Label>
                      <Form.Select
                        name="agent_type_id"
                        value={formData.agent_type_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select agent type</option>
                        {agentTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Commission Rate</Form.Label>
                      <Form.Select
                        name="agent_commission_id"
                        value={formData.agent_commission_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select commission rate</option>
                        {agentCommissions.map(commission => (
                          <option key={commission.id} value={commission.id}>
                            {commission.commission_rate}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="address1"
                        value={formData.address1}
                        onChange={handleInputChange}
                        placeholder="Enter address line 1"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="address2"
                        value={formData.address2}
                        onChange={handleInputChange}
                        placeholder="Enter address line 2"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="Enter state"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zipcode</Form.Label>
                      <Form.Control
                        type="text"
                        name="zipcode"
                        value={formData.zipcode}
                        onChange={handleInputChange}
                        placeholder="Enter zipcode"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>County</Form.Label>
                      <Form.Control
                        type="text"
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        placeholder="Enter county"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <div>
                        <Form.Check
                          type="checkbox"
                          name="active"
                          checked={formData.active}
                          onChange={handleInputChange}
                          label="Active"
                          id="active-checkbox"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone1"
                        value={formData.phone1}
                        onChange={handleInputChange}
                        placeholder="Enter primary phone"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Secondary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone2"
                        value={formData.phone2}
                        onChange={handleInputChange}
                        placeholder="Enter secondary phone"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col className="text-end">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleClear}
                      className="me-2"
                    >
                      Clear Form
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Agent'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AgentsAddScreen;
